import { type PrimitiveAtom, useAtomValue } from 'jotai'
import React from 'react'
import { FormProvider, type UseFormReturn } from 'react-hook-form'

import { SuspenseDialog } from '~/components/elements/SuspenseDialog'
import { VStack } from '~/components/elements/VStack'
import { useTurnOffAtom } from '~/hooks/jotai'

import { isVisibleLoadDialogAtom, isVisibleSaveDialogAtom } from './atoms'
import {
  EditFilterDialogActions,
  type EditFilterDialogActionsProps,
} from './components/EditFilterDialogActions'
import {
  LoadFilterDialog,
  type LoadFilterDialogProps,
} from './components/LoadFilterDialog'
import {
  SaveFilterDialog,
  type SaveFilterDialogProps,
} from './components/SaveFilterDialog'
import styles from './EditFilterDialog.css'
import { type InputValues, type SavedFilter } from './utils'

export type EditFilterDialogProps<TFilterItem> = {
  executing?: boolean
  filterGroupsInput: React.ReactNode
  filters: SavedFilter[]
  isVisibleEditDialogAtom: PrimitiveAtom<boolean>
  methods: UseFormReturn<InputValues<TFilterItem>>
  onDelete?: LoadFilterDialogProps<TFilterItem>['onDelete']
  onExecute?: EditFilterDialogActionsProps<TFilterItem>['onExecute']
  onLoad?: LoadFilterDialogProps<TFilterItem>['onLoad']
  onSave?: SaveFilterDialogProps<TFilterItem>['onSave']
  title: string
}

export const EditFilterDialog = <TFilterItem,>({
  executing,
  filterGroupsInput,
  filters,
  isVisibleEditDialogAtom,
  methods,
  onDelete,
  onExecute,
  onLoad,
  onSave,
  title,
}: EditFilterDialogProps<TFilterItem>) => {
  const closeEditDialog = useTurnOffAtom(isVisibleEditDialogAtom)

  const isVisibleSaveDialog = useAtomValue(isVisibleSaveDialogAtom)
  const isVisibleLoadDialog = useAtomValue(isVisibleLoadDialogAtom)

  return (
    <FormProvider {...methods}>
      <SuspenseDialog
        actions={
          <EditFilterDialogActions
            filters={filters}
            isVisibleEditDialogAtom={isVisibleEditDialogAtom}
            onExecute={onExecute}
          />
        }
        className={styles.root}
        executing={executing}
        title={title}
        onClose={closeEditDialog}
      >
        <VStack className={styles.container} spacing={4}>
          {filterGroupsInput}
        </VStack>

        {isVisibleSaveDialog && (
          <SaveFilterDialog
            filters={filters}
            isVisibleSaveDialogAtom={isVisibleSaveDialogAtom}
            onSave={onSave}
          />
        )}

        {isVisibleLoadDialog && (
          <LoadFilterDialog
            filters={filters}
            onDelete={onDelete}
            onLoad={onLoad}
          />
        )}
      </SuspenseDialog>
    </FormProvider>
  )
}
